import DOMPurify from 'dompurify';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import useBroadcastQuery from '../api/queries/useBroadcastQuery';

function Header() {
    const [menuOpen, setMenuOpen] = useState(false);

    const { t, i18n } = useTranslation();
    const { t: url } = useTranslation('urls');
    const navigate = useNavigate();
    const { isLoading, data } = useBroadcastQuery();
    const [broadcastOpen, setBroadcastOpen] = useState(true);

    const changeLang = (iso: string) => {
        i18n.changeLanguage(iso);
        navigate('/');
    };

    return (
        <>
            {broadcastOpen && !isLoading && data && data.length ? (
                <div className="fixed left-0 top-0 z-10 flex w-full flex-col items-center justify-center bg-warm/95 px-4 py-4 md:flex-row md:px-24">
                    <div className="flex-0 mb-2 md:mb-0 md:mr-6">
                        <svg
                            width="32"
                            height="29"
                            viewBox="0 0 32 29"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M31.361 21.8271L20.1978 2.41523C19.7464 1.67479 19.1244 1.06544 18.3893 0.643341C17.6542 0.221246 16.8296 0 15.9915 0C15.1534 0 14.3288 0.221246 13.5937 0.643341C12.8586 1.06544 12.2367 1.67479 11.7853 2.41523L0.622063 21.8271C0.227716 22.5163 0.0133808 23.3023 0.000606453 24.106C-0.0121679 24.9097 0.177069 25.7027 0.549291 26.4054C0.979635 27.1963 1.60253 27.8532 2.35433 28.309C3.10613 28.7648 3.95986 29.0032 4.82829 28.9997H27.1548C28.0175 29.0094 28.8674 28.7802 29.6187 28.3354C30.3699 27.8907 30.996 27.246 31.4338 26.4664C31.8169 25.7564 32.0122 24.952 31.9994 24.1364C31.9866 23.3207 31.7662 22.5235 31.361 21.8271ZM28.8867 24.9556C28.7118 25.2708 28.458 25.5299 28.1527 25.7048C27.8474 25.8796 27.5023 25.9636 27.1548 25.9475H4.82829C4.48076 25.9636 4.13567 25.8796 3.83038 25.7048C3.52509 25.5299 3.27124 25.2708 3.09631 24.9556C2.96857 24.7236 2.90132 24.4604 2.90132 24.1925C2.90132 23.9246 2.96857 23.6615 3.09631 23.4295L14.2741 4.00236C14.475 3.72416 14.7346 3.49854 15.0325 3.3432C15.3305 3.18786 15.6588 3.10701 15.9915 3.10701C16.3243 3.10701 16.6525 3.18786 16.9505 3.3432C17.2485 3.49854 17.5081 3.72416 17.709 4.00236L28.8722 23.4142C29.0049 23.6467 29.0761 23.9119 29.0787 24.1828C29.0813 24.4537 29.015 24.7204 28.8867 24.9556Z"
                                fill="#A0310F"
                            />
                            <path
                                d="M15.9916 22.8953C16.7954 22.8953 17.447 22.2121 17.447 21.3693C17.447 20.5264 16.7954 19.8432 15.9916 19.8432C15.1877 19.8432 14.5361 20.5264 14.5361 21.3693C14.5361 22.2121 15.1877 22.8953 15.9916 22.8953Z"
                                fill="#A0310F"
                            />
                            <path
                                d="M15.9916 9.16054C15.6056 9.16054 15.2354 9.32133 14.9624 9.60752C14.6895 9.89372 14.5361 10.2819 14.5361 10.6866V16.791C14.5361 17.1957 14.6895 17.5839 14.9624 17.8701C15.2354 18.1563 15.6056 18.3171 15.9916 18.3171C16.3776 18.3171 16.7478 18.1563 17.0207 17.8701C17.2937 17.5839 17.447 17.1957 17.447 16.791V10.6866C17.447 10.2819 17.2937 9.89372 17.0207 9.60752C16.7478 9.32133 16.3776 9.16054 15.9916 9.16054Z"
                                fill="#A0310F"
                            />
                        </svg>
                    </div>

                    <div className="mt-4 grow leading-6 text-[#A0310F]">
                        {data.map((message) => (
                            <div
                                className="mb-4"
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(message),
                                }}
                            />
                        ))}
                    </div>

                    <button
                        type="button"
                        className="mt-2 border-b-2 border-[#A0310F] pb-1 font-bold uppercase tracking-[0.25rem] text-[#A0310F] hover:border-dark hover:text-dark md:mt-0"
                        onClick={() => setBroadcastOpen(false)}>
                        {t('header.close')}
                    </button>
                </div>
            ) : null}
            {menuOpen && (
                <div className="static left-0 top-0 h-screen w-full bg-warm p-[24px]">
                    <div className="flex w-full items-center justify-between">
                        <button
                            type="button"
                            onClick={() => setMenuOpen(false)}
                            className="flex items-start font-bold uppercase tracking-[4px]">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none">
                                <path
                                    d="M2.125 2.125L8.5 8.5M14.875 14.875L8.5 8.5M8.5 8.5L14.875 2.125M8.5 8.5L2.125 14.875"
                                    stroke="black"
                                    strokeWidth="2"
                                />
                            </svg>
                            <span className="pl-2 leading-[18px]">
                                {t('header.close')}
                            </span>
                        </button>
                        <div>
                            <button
                                type="button"
                                onClick={() => changeLang('cs')}>
                                CZ
                            </button>{' '}
                            /{' '}
                            <button
                                type="button"
                                onClick={() => changeLang('en')}>
                                EN
                            </button>
                        </div>
                    </div>
                    <div className="mt-[48px] flex w-full flex-col text-[36px]">
                        <Link
                            onClick={() => setMenuOpen(false)}
                            to={`/${url('search')}`}>
                            {t('header.search')}
                        </Link>
                        <Link
                            onClick={() => setMenuOpen(false)}
                            to={`/${url('category')}/2-${url(
                                'categories.new',
                            )}`}>
                            {t('nav.new')}
                        </Link>
                        <Link
                            onClick={() => setMenuOpen(false)}
                            to={`/${url('category')}/16-${url(
                                'categories.prints',
                            )}`}>
                            {t('nav.prints')}
                        </Link>
                        <Link
                            onClick={() => setMenuOpen(false)}
                            to={`/${url('category')}/17-${url(
                                'categories.books',
                            )}`}>
                            {t('nav.books')}
                        </Link>
                        <Link
                            onClick={() => setMenuOpen(false)}
                            to={`/${url('category')}/18-${url(
                                'categories.gifts',
                            )}`}>
                            {t('nav.gifts')}
                        </Link>
                        <Link
                            onClick={() => setMenuOpen(false)}
                            to={`/${url('cart')}`}>
                            {t('header.cart')}
                        </Link>
                        <Link
                            className="mt-[20px] text-base font-medium"
                            onClick={() => setMenuOpen(false)}
                            to={`/${url('terms')}`}>
                            {t('footer.terms')}
                        </Link>
                        <a
                            className="mt-[20px] text-base font-medium"
                            href={`https://muo.cz/${url('contact')}`}
                            rel="noopener noreferrer">
                            {t('footer.contact')}
                        </a>
                    </div>
                </div>
            )}

            <div className="flex w-full items-center px-5 py-6 md:px-0">
                <div className="flex flex-auto items-center md:w-1/3">
                    <button
                        type="button"
                        className="md:hidden"
                        onClick={() => setMenuOpen((prev) => !prev)}>
                        <svg
                            width="36"
                            height="25"
                            viewBox="0 0 36 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M14.776 0.127998C12.856 0.127998 11.416 1.312 8.856 3.744L8.952 3.84C9.784 3.232 10.68 2.848 12.248 2.848C14.968 2.848 16.792 4.096 16.792 7.648V24H20.504V6.848C20.504 2.176 18.136 0.127998 14.776 0.127998ZM35.704 20.992L35.64 20.928C35 21.408 34.552 21.632 33.752 21.632C32.12 21.632 30.136 19.904 28.728 19.2L29.752 18.176C31.896 16.032 32.6 15.072 32.6 11.968V7.04C32.6 2.176 30.2 0.127998 26.712 0.127998C24.728 0.127998 23.544 1.024 20.696 3.904L20.792 4C21.784 3.2 22.776 2.848 24.408 2.848C27 2.848 28.888 4.128 28.888 7.648V15.456C28.888 18.304 28.44 18.848 27 20.224L25.624 21.536V21.664C27.064 22.4 29.208 24.512 31.032 24.512C32.408 24.512 33.176 23.744 35.704 20.992ZM4.632 4.032H7.576V4.128C5.336 6.112 4.568 7.264 4.568 9.504V16.192C4.568 18.528 4.408 19.2 3.576 20.192L3.672 20.256L5.016 19.168C7.736 16.96 8.28 15.84 8.28 13.184V4.256C8.28 2.688 8.536 1.696 9.144 0.639999H6.52C4.792 0.639999 3.544 1.152 2.008 2.944L0.28 4.928L0.376 5.024C1.304 4.192 2.008 4.032 4.632 4.032ZM10.712 21.376C8.856 21.376 7.16 20.192 5.24 20.192C3.672 20.192 2.616 21.376 0.408 23.968L0.472 24.032C1.112 23.52 1.56 23.296 2.52 23.296C4.408 23.296 6.136 24.512 7.8 24.512C9.4 24.512 10.136 23.488 12.6 20.768L12.536 20.704C11.96 21.152 11.48 21.376 10.712 21.376Z"
                                fill="black"
                            />
                        </svg>
                    </button>
                    <a
                        href={i18n.resolvedLanguage === 'en' ? '/en' : '/'}
                        className="mr-2 hidden pr-2 md:inline-block md:border-r-2 md:border-black">
                        <svg
                            width="83"
                            height="25"
                            viewBox="0 0 83 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M14.776 0.127998C12.856 0.127998 11.416 1.312 8.856 3.744L8.952 3.84C9.784 3.232 10.68 2.848 12.248 2.848C14.968 2.848 16.792 4.096 16.792 7.648V24H20.504V6.848C20.504 2.176 18.136 0.127998 14.776 0.127998ZM35.704 20.992L35.64 20.928C35 21.408 34.552 21.632 33.752 21.632C32.12 21.632 30.136 19.904 28.728 19.2L29.752 18.176C31.896 16.032 32.6 15.072 32.6 11.968V7.04C32.6 2.176 30.2 0.127998 26.712 0.127998C24.728 0.127998 23.544 1.024 20.696 3.904L20.792 4C21.784 3.2 22.776 2.848 24.408 2.848C27 2.848 28.888 4.128 28.888 7.648V15.456C28.888 18.304 28.44 18.848 27 20.224L25.624 21.536V21.664C27.064 22.4 29.208 24.512 31.032 24.512C32.408 24.512 33.176 23.744 35.704 20.992ZM4.632 4.032H7.576V4.128C5.336 6.112 4.568 7.264 4.568 9.504V16.192C4.568 18.528 4.408 19.2 3.576 20.192L3.672 20.256L5.016 19.168C7.736 16.96 8.28 15.84 8.28 13.184V4.256C8.28 2.688 8.536 1.696 9.144 0.639999H6.52C4.792 0.639999 3.544 1.152 2.008 2.944L0.28 4.928L0.376 5.024C1.304 4.192 2.008 4.032 4.632 4.032ZM10.712 21.376C8.856 21.376 7.16 20.192 5.24 20.192C3.672 20.192 2.616 21.376 0.408 23.968L0.472 24.032C1.112 23.52 1.56 23.296 2.52 23.296C4.408 23.296 6.136 24.512 7.8 24.512C9.4 24.512 10.136 23.488 12.6 20.768L12.536 20.704C11.96 21.152 11.48 21.376 10.712 21.376ZM46.5825 24.448C52.3745 24.448 55.4785 21.696 55.4785 14.688V0.639999H51.8945V14.752C51.8945 19.36 50.3265 21.408 46.5825 21.408C42.8385 21.408 41.2705 19.36 41.2705 14.752V0.639999H37.6865V14.688C37.6865 21.696 40.7905 24.448 46.5825 24.448ZM71.1178 24.448C78.1258 24.448 82.6698 19.424 82.6698 12.352C82.6698 5.312 78.0618 0.191999 71.1178 0.191999C64.1738 0.191999 59.5658 5.312 59.5658 12.352C59.5658 19.424 64.1738 24.448 71.1178 24.448ZM71.1178 21.408C66.2218 21.408 63.3098 17.504 63.3098 12.288C63.3098 7.168 66.2218 3.296 71.1178 3.296C75.9498 3.296 78.9258 7.168 78.9258 12.32C78.9258 17.536 75.9498 21.408 71.1178 21.408Z"
                                fill="black"
                            />
                        </svg>
                    </a>
                    <Link
                        to="/"
                        className="hidden align-middle text-2xl leading-8 md:inline-block">
                        {t('header.shop')}
                    </Link>
                </div>

                <div className="flex-auto text-center md:w-1/3">
                    <Link to={`/${url('search')}`}>{t('header.search')}</Link>
                </div>

                <div className="flex-auto text-right md:w-1/3">
                    <Link to={`/${url('cart')}`}>{t('header.cart')}</Link>
                </div>
            </div>
        </>
    );
}

export default Header;
